/**
 * Authentication Context
 * Fornisce stato autenticazione globale all'applicazione
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useBackendWarmup } from '@/hooks/useBackendWarmup';
import { logger } from '@/lib/logger';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { 
  AuthenticatedUser, 
  verifyAuthenticatedUser,
  login as authLogin,
  logout as authLogout,
  hasPlatformPermission
} from '@/services/authService';
import { PermissionsHelper } from '@/types/permissions';
import { hasStructureScopeForApplication } from '@/features/dipendenze/utils/structureScope';

export type AuthContextType = {
  user: AuthenticatedUser | null;
  loading: boolean;
  error: string | null;
  unauthorized: { email: string; reason: string } | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  /** Compatibilità con il ruolo legacy */
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  platformAccessVerified: boolean;
  isSuperAdmin: boolean;
  /** Hub per cui l'utente è Admin di Area (accesso completo senza essere Super Admin) */
  adminScope: string[];
  /** True se l'utente è Admin di Area per l'hub indicato (o Super Admin) */
  isAreaAdmin: (hubId: string) => boolean;
  isAuthorized: boolean;
  checkPermission: (action: string) => boolean;
  hasScope: (scope: string) => boolean;
  hasPlatformPermission: (permission: string) => boolean;
  // Nuovi metodi per permessi Hub/App
  canViewHub: (hubId: import('@/types/permissions').HubId) => boolean;
  canAccessApp: (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId) => boolean;
  /** False solo se è noto che l'utente non ha nessuna struttura assegnata per l'applicazione Dipendenze indicata */
  hasStructureScope: (appId: string) => boolean;
  getVisibleHubs: () => import('@/types/permissions').HubId[];
  getAccessibleApps: (hubId: import('@/types/permissions').HubId) => import('@/types/permissions').AppId[];
  // CRUD permissions
  canCreate: (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId) => boolean;
  canRead: (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId) => boolean;
  canUpdate: (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId) => boolean;
  canDelete: (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato all\'interno di AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState<{ email: string; reason: string } | null>(null);

  useBackendWarmup(user, loading);

  useEffect(() => {
    logger.debug('🔧 AuthContext: Inizializzazione listener autenticazione');
    
    // Ascolta i cambiamenti dello stato di autenticazione
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      logger.debug('🔄 AuthContext: Cambio stato auth', firebaseUser?.email || 'nessun utente');
      setLoading(true);
      setError(null);

      if (firebaseUser) {
        try {
          logger.debug('🔍 AuthContext: Avvio verifica autorizzazioni per', firebaseUser.email);
          // Verifica autorizzazioni utente
          const authenticatedUser = await verifyAuthenticatedUser(firebaseUser);
          
          logger.debug('📋 AuthContext: Risultato verifica:', authenticatedUser ? `${authenticatedUser.email} - ${authenticatedUser.role}` : 'NON AUTORIZZATO');
          
          if (authenticatedUser) {
            setUser(authenticatedUser);
            setUnauthorized(null);
            logger.debug('✅ AuthContext: Utente autorizzato e impostato');
            console.log('[AuthProvider] Authenticated:', {
              email: authenticatedUser.email,
              uid: authenticatedUser.uid,
              role: authenticatedUser.role,
              isSuperAdmin: authenticatedUser.isSuperAdmin,
              platformPermissions: authenticatedUser.platformPermissions,
              scopes: authenticatedUser.scopes,
            });
          } else {
            // Utente non autorizzato - mostra pagina di blocco
            console.error('❌ AuthContext: Utente NON autorizzato, mostro pagina di blocco');
            setUser(null);
            setUnauthorized({
              email: firebaseUser.email || 'unknown',
              reason: 'not_in_allowed_groups'
            });
            setError('Accesso negato: utente non autorizzato per questo dominio');
          }
        } catch (err) {
          console.error('❌ AuthContext: Errore verifica utente:', err);
          setError('Errore durante la verifica delle autorizzazioni');
          setUser(null);
        }
      } else {
        logger.debug('ℹ️ AuthContext: Nessun utente, reset stato');
        setUser(null);
      }

      setLoading(false);
    });

    return () => {
      logger.debug('🔧 AuthContext: Cleanup listener');
      unsubscribe();
    };
  }, []);

  const login = async () => {
    try {
      setLoading(true);
      setError(null);
      const authenticatedUser = await authLogin();
      setUser(authenticatedUser);
    } catch (err: any) {
      setError(err.message || 'Errore durante il login');
      setUser(null);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authLogout();
      setUser(null);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore durante il logout');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const checkPermission = (action: string): boolean => {
    if (!user || !user.isAuthorized) {
      return false;
    }

    // Admin ha tutti i permessi
    if (user.isAdmin) {
      return true;
    }

    // Verifica permesso string-based platform
    if (hasPlatformPermission(user, action)) {
      return true;
    }

    // Fallback: verifica scope generico nell'array scopes dell'utente
    const userScopes = (user as any).scopes || [];
    return userScopes.includes(action);
  };

  // Helper per verificare scope specifico
  const hasScope = (scope: string): boolean => {
    if (!user || !user.isAuthorized) {
      return false;
    }

    // Admin ha tutti gli scope
    if (user.isAdmin) {
      return true;
    }

    // Verifica scope nell'array scopes dell'utente
    const userScopes = (user as any).scopes || [];
    return userScopes.includes(scope);
  };

  // Metodi per permessi Hub/App
  const canViewHub = (hubId: import('@/types/permissions').HubId): boolean => {
    if (!user) return false;
    const helper = new PermissionsHelper(user.permissions);
    const result = helper.canViewHub(hubId);
    if (import.meta.env.VITE_AUTH_DEBUG === 'true') {
      logger.debug('AuthContext Debug - canViewHub', { email: user.email, hubId, result });
    }
    return result;
  };

  const canAccessApp = (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId): boolean => {
    if (!user) return false;
    const helper = new PermissionsHelper(user.permissions);
    const result = helper.canAccessApp(hubId, appId);
    if (import.meta.env.VITE_AUTH_DEBUG === 'true') {
      logger.debug('AuthContext Debug - canAccessApp', { email: user.email, hubId, appId, result });
    }
    return result;
  };

  const hasStructureScope = (appId: string): boolean => (
    hasStructureScopeForApplication(user?.dipendenzeStructureAccess, appId)
  );

  const getVisibleHubs = (): import('@/types/permissions').HubId[] => {
    if (!user) return [];
    const helper = new PermissionsHelper(user.permissions);
    return helper.getVisibleHubs();
  };

  const getAccessibleApps = (hubId: import('@/types/permissions').HubId): import('@/types/permissions').AppId[] => {
    if (!user) return [];
    const helper = new PermissionsHelper(user.permissions);
    return helper.getAccessibleApps(hubId);
  };

  const canCreate = (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId): boolean => {
    if (!user) return false;
    return new PermissionsHelper(user.permissions).canCreate(hubId, appId);
  };

  const canRead = (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId): boolean => {
    if (!user) return false;
    return new PermissionsHelper(user.permissions).canRead(hubId, appId);
  };

  const canUpdate = (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId): boolean => {
    if (!user) return false;
    return new PermissionsHelper(user.permissions).canUpdate(hubId, appId);
  };

  const canDelete = (hubId: import('@/types/permissions').HubId, appId: import('@/types/permissions').AppId): boolean => {
    if (!user) return false;
    return new PermissionsHelper(user.permissions).canDelete(hubId, appId);
  };

  const isAreaAdmin = (hubId: string): boolean => {
    if (!user) return false;
    if (user.isSuperAdmin) return true;
    return (user.adminScope || []).includes(hubId);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    unauthorized,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin ?? false,
    isPlatformAdmin: user?.isPlatformAdmin === true && user.platformAccessVerified,
    platformAccessVerified: user?.platformAccessVerified === true,
    isSuperAdmin: user?.isSuperAdmin ?? false,
    adminScope: user?.adminScope ?? [],
    isAreaAdmin,
    isAuthorized: user?.isAuthorized ?? false,
    checkPermission,
    hasScope,
    canViewHub,
    canAccessApp,
    hasStructureScope,
    getVisibleHubs,
    getAccessibleApps,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    hasPlatformPermission: (permission: string) => hasPlatformPermission(user, permission)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
